import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import ContentBlock from "../Layout/ContentBlock";
import FilterMenu from "../Layout/FilterMenu";
import TableFilterBox from "../common/TableFilterBox";
import ContentValidation from "../common/ContentValidation";
import Pagination from "../Dashboard/items/Pagination";
import {
    ButtonDelete,
    ButtonSmall,
    SelectForm,
} from "../common/Buttons";
import { IconSearch, IconDuplicate } from "../Layout/Icons";
import InputText from "../orders/accepted_orders/InputFilter";
import { handleKeyDown, onChangeUseState } from "../../helpers/FunctionsGlobal";
import { antdMessageSuccess, antdMessageError } from "../../helpers/Utils";
import SwitchComp from "../common/SwitchComp";
import TabUi from "./TabUi";
import SystemRulesModal from "./SystemRulesModal";
import { Modal } from "antd";
import { ExclamationCircleOutlined } from "@ant-design/icons";

const { confirm } = Modal;

const filterInitial = {
    rule_title: "",
    rule_code: "",
    shop: "",
    status: "",
};

const DEFAULT_PER_PAGE = 50;

export default function SystemRules({ tabId, setTabId }) {
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(DEFAULT_PER_PAGE);
    const [filters, setFilters] = useState(filterInitial);
    const [totals, setTotals] = useState(0);
    const [loading, setLoading] = useState(false);
    const [modalPreview, setModalPreview] = useState(false);
    const [data, setData] = useState([]);
    const [shops, setShops] = useState([]);
    const [rules, setRules] = useState([]);

    const fetchShops = useCallback(async () => {
        try {
            const response = await axios.get("/api/v1/order_shops");
            const shopsData = response.data?.data || response.data || [];
            setShops(
                shopsData.map((shop) => ({
                    label: shop.title || shop.identifier || `Shop ${shop.id}`,
                    value: shop.id,
                }))
            );
        } catch (error) {
            console.error("Error fetching shops:", error);
        }
    }, []);

    const fetchRules = useCallback(async () => {
        try {
            const response = await axios.get("/api/v1/system_rules/rules");
            setRules(response.data?.data || []);
        } catch (error) {
            console.error("Error fetching rules:", error);
        }
    }, []);

    const fetchSystemRules = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            params.append("page", page);
            params.append("per_page", perPage);

            if (filters.rule_title) {
                params.append("q[rule_title_cont]", filters.rule_title);
            }
            if (filters.rule_code) {
                params.append("q[rule_code_eq]", filters.rule_code);
            }
            if (filters.status !== "") {
                params.append("q[status_eq]", filters.status);
            }

            const response = await axios.get(`/api/v1/system_rules?${params.toString()}`);
            const responseData = response.data?.data || [];
            setData(responseData);
            setTotals(response.data?.total || responseData.length);
        } catch (error) {
            console.error("Error fetching system rules:", error);
            antdMessageError("Failed to fetch system rules");
        } finally {
            setLoading(false);
        }
    }, [page, perPage, filters]);

    useEffect(() => {
        fetchShops();
        fetchRules();
    }, [fetchShops, fetchRules]);

    useEffect(() => {
        fetchSystemRules();
    }, [fetchSystemRules]);

    const onFilter = () => {
        setPage(1);
        fetchSystemRules();
    };

    const handleClearFilter = () => {
        setFilters(filterInitial);
        setPage(1);
    };

    const handleDuplicate = async (id) => {
        try {
            const response = await axios.get(`/api/v1/system_rules/${id}`);
            const ruleData = response.data;

            const duplicateData = {
                ...ruleData,
                rule_title: `${ruleData.rule_title} (Copy)`,
                system_rule_order_shops_attributes: ruleData.order_shops?.map((shop) => ({
                    order_shop_id: shop.id,
                })) || [],
            };
            delete duplicateData.id;
            delete duplicateData.order_shops;
            delete duplicateData.full_name;
            delete duplicateData.created_at;
            delete duplicateData.updated_at;

            await axios.post("/api/v1/system_rules", { system_rule: duplicateData });
            antdMessageSuccess("System rule duplicated successfully");
            fetchSystemRules();
        } catch (error) {
            console.error("Error duplicating system rule:", error);
            antdMessageError(error.response?.data?.message || "Failed to duplicate system rule");
        }
    };

    const handleDelete = async (id) => {
        try {
            await axios.delete(`/api/v1/system_rules/${id}`);
            antdMessageSuccess("System rule deleted successfully");
            fetchSystemRules();
        } catch (error) {
            console.error("Error deleting system rule:", error);
            antdMessageError(error.response?.data?.message || "Failed to delete system rule");
        }
    };

    const handleStatusChange = async (id, currentStatus) => {
        try {
            await axios.put(`/api/v1/system_rules/${id}`, {
                system_rule: { status: !currentStatus }
            });
            antdMessageSuccess("Status updated successfully");
            fetchSystemRules();
        } catch (error) {
            console.error("Error updating status:", error);
            antdMessageError(error.response?.data?.message || "Failed to update status");
        }
    };

    const getRuleTitle = (code) => {
        const rule = rules.find((r) => r.code === code);
        return rule?.title || code;
    };

    const formatPeriod = (period) => {
        if (!period || !Array.isArray(period) || period.length === 0) return "-";
        const start = period[0] || "";
        const end = period[1] || "∞";
        return start ? `${start} - ${end}` : "-";
    };

    const formatShops = (orderShops) => {
        if (!orderShops || orderShops.length === 0) return "-";
        return orderShops.map((shop) => shop.title || shop.identifier).join(", ");
    };

    const renderCustomButton = () => {
        return (
            <button
                type="button"
                className="btn btn-sm btn-primary ml-3 float-left"
                onClick={() => setModalPreview(0)}
            >
                {"Create new"}
            </button>
        );
    };

    return (
        <>
            <ContentBlock>
                <FilterMenu
                    totals={totals}
                    length={data.length}
                    text2="System rules"
                    countPage={perPage}
                    onChangeCountPage={setPerPage}
                />
            </ContentBlock>

            <div className="col-12">
                <TabUi tabId={tabId} setTabId={setTabId} />
            </div>

            <ContentBlock containerClass="py-1">
                <TableFilterBox
                    add={false}
                    filter={true}
                    onFilter={() => onFilter()}
                    clearFilter={handleClearFilter}
                    button1={renderCustomButton()}
                >
                    <div className="px-1">
                        <InputText
                            style={{ width: 165, fontSize: "0.75rem" }}
                            name="rule_title"
                            placeholder="Rule title"
                            value={filters.rule_title}
                            onKeyDown={(e) => handleKeyDown(e, onFilter)}
                            onChange={onChangeUseState(setFilters)}
                        />
                    </div>
                    <div className="px-1">
                        <SelectForm
                            placeholder="Rule type"
                            style={{ width: 165 }}
                            dropdownMatchSelectWidth={false}
                            options={[
                                { label: "All", value: "" },
                                ...rules.map((rule) => ({
                                    label: rule.title,
                                    value: rule.code,
                                })),
                            ]}
                            onChange={(rule_code) =>
                                setFilters((prev) => ({ ...prev, rule_code }))
                            }
                            value={filters.rule_code || undefined}
                        />
                    </div>
                    <div className="px-1">
                        <SelectForm
                            placeholder="Status"
                            style={{ width: 165 }}
                            dropdownMatchSelectWidth={false}
                            options={[
                                { label: "All", value: "" },
                                { label: "Active", value: "true" },
                                { label: "Inactive", value: "false" },
                            ]}
                            onChange={(status) =>
                                setFilters((prev) => ({ ...prev, status }))
                            }
                            value={filters.status || undefined}
                        />
                    </div>
                </TableFilterBox>
                <div>
                    <ContentValidation loader={loading} array={data}>
                        <div className="table-responsive" style={{ overflow: "hidden" }}>
                            <table className="table mt-3">
                                <thead>
                                    <tr className="text-muted">
                                        <th className="text-black">Rule title</th>
                                        <th className="text-black">System rule base</th>
                                        <th className="text-black">Shop</th>
                                        <th className="text-black">Period</th>
                                        <th className="text-black">Status</th>
                                        <th className="text-black"></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {data.map((item) => (
                                        <SystemRuleRow
                                            key={item.id}
                                            id={item.id}
                                            rule_title={item.rule_title}
                                            system_rule_base={getRuleTitle(item.rule_code)}
                                            shop={formatShops(item.order_shops)}
                                            period={formatPeriod(item.period)}
                                            status={item.status}
                                            modalFunc={setModalPreview}
                                            onDuplicate={handleDuplicate}
                                            onDelete={handleDelete}
                                            onStatusChange={handleStatusChange}
                                        />
                                    ))}
                                </tbody>
                            </table>
                            <Pagination
                                page={page}
                                pageCount={perPage}
                                totals={totals}
                                setPagination={setPage}
                            />
                        </div>
                    </ContentValidation>
                </div>
            </ContentBlock>

            {typeof modalPreview === "number" && (
                <SystemRulesModal
                    id={modalPreview}
                    modalFunc={setModalPreview}
                    shops={shops}
                    rules={rules}
                    onSaveSuccess={fetchSystemRules}
                />
            )}
        </>
    );
}

const SystemRuleRow = ({
    id,
    rule_title,
    system_rule_base,
    shop,
    period,
    status,
    modalFunc,
    onDuplicate,
    onDelete,
    onStatusChange,
}) => {
    return (
        <tr className="align-middle tableColumn">
            <td className="align-middle">{rule_title}</td>
            <td className="align-middle">{system_rule_base}</td>
            <td className="align-middle">{shop}</td>
            <td className="align-middle">{period}</td>
            <td className="align-middle">
                <SwitchComp
                    checked={status}
                    onChange={() => onStatusChange(id, status)}
                />
            </td>
            <td className="align-middle">
                <div className="d-flex justify-content-end">
                    <ButtonSmall
                        type="outline"
                        onClick={() => {
                            confirm({
                                title: "Are you sure?",
                                icon: <ExclamationCircleOutlined className="text-primary" />,
                                content: "You will duplicate this system rule",
                                onOk() {
                                    onDuplicate(id);
                                },
                                onCancel() {
                                    console.log("Cancel");
                                },
                            });
                        }}
                    >
                        <IconDuplicate color="#03B0F7" />
                    </ButtonSmall>
                    <ButtonSmall
                        className={"mx-2"}
                        onClick={() => modalFunc(id)}
                    >
                        <IconSearch color="#fff" />
                    </ButtonSmall>
                    <ButtonDelete
                        onDelete={() =>
                            confirm({
                                title: "Are you sure?",
                                icon: <ExclamationCircleOutlined className="text-primary" />,
                                content: "You will not be able to undo this action",
                                onOk() {
                                    onDelete(id);
                                },
                                onCancel() {
                                    console.log("Cancel");
                                },
                            })
                        }
                    />
                </div>
            </td>
        </tr>
    );
};
